import * as actionTypes from "./actionTypes";
import instance from "./instance";

// Actions
import { linkWithLanguage } from "components/helpers/commonLinks";
import { getCreditsAPI } from "redux/services/applicants";
import {
  apiErrorCatcher,
  createQuery,
  handleFileDownload,
} from "redux/slices/common-action-utils";
import { handleError, resetErrors } from "../actions";

export const setApplicant = (applicant) => ({
  type: actionTypes.SET_APPLICANT,
  payload: applicant,
});

export const getApplicant = (userID) => async (dispatch) => {
  try {
    const res = await instance.get(`/applicants/${userID}/`);
    const applicant = res.data;

    dispatch({
      type: actionTypes.SET_APPLICANT_COMPLEX,
      payload: applicant,
    });
    return applicant;
  } catch (error) {
    error.response ? console.error(error.response.data) : console.error(error);
  }
};

export const fetchPhotoVideoConsent = (applicantIds) => async () => {
  const response = await instance.get("/applicants/photo-video-consent/", {
    params: { applicant_ids: applicantIds.join(",") },
  });
  return response.data;
};

export const fetchPhotoVideoConsentFamily = () => async () => {
  const response = await instance.get("/applicants/photo-video-consent/", {
    params: { family: "1" },
  });
  return response.data;
};

export const fetchParentalPhotoVideoConsent = (token) => async () => {
  const response = await instance.get(
    `/applicants/photo-video-consent/${token}/`
  );
  return response.data;
};

export const saveParentalPhotoVideoConsent = (token, photoVideoConsent) => async () => {
  const response = await instance.post(
    `/applicants/photo-video-consent/${token}/`,
    { photo_video_consent: photoVideoConsent }
  );
  return response.data;
};

export const savePhotoVideoConsent = (answers, overwrite = false) => async (dispatch, getState) => {
  const response = await instance.post("/applicants/photo-video-consent/", {
    answers,
    overwrite,
  });
  const current = getState().rootAuth?.applicant;
  if (current) {
    const match = answers.find((answer) => answer.applicant_id === current.id);
    const saved = (response.data || []).find(
      (row) => row.id === current.id
    );
    if (match || saved) {
      dispatch(
        setApplicant({
          ...current,
          photo_video_consent: saved
            ? saved.photo_video_consent
            : match.photo_video_consent,
          photo_video_consent_status: saved?.consent_status,
        })
      );
    }
  }
  return response.data;
};

export const getApplicantSimplified = (userID) => async (dispatch) => {
  try {
    const res = await instance.get(`/applicants/${userID}/simple/`);
    const applicant = res.data;

    dispatch(setApplicant(applicant));
    return applicant;
  } catch (error) {
    error.response ? console.error(error.response.data) : console.error(error);
  }
};

export const getApplicantInfo = (userID) => async () => {
  try {
    const response = await instance.get(`/applicants/${userID}/info/`);
    return response.data;
  } catch (error) {
    error.response ? console.error(error.response.data) : console.error(error);
  }
};

export const GetApplicantProfile = (userID) => async () => {
  try {
    const response = await instance.get(`/applicants/${userID}/profile/`);
    return response.data;
  } catch (error) {
    error.response ? console.error(error.response.data) : console.error(error);
  }
};

export const GetLeaderBoardInfo = (userID) => async () => {
  try {
    const response = await instance.get(`/applicants/${userID}/leaderboard/`);
    return response.data;
  } catch (error) {
    error.response ? console.error(error.response.data) : console.error(error);
  }
};

export const UpdateApplicantProfile =
  (userID, profileData, history) => async (dispatch) => {
    try {
      const formData = new FormData();

      Object.entries(profileData).forEach(([key, value]) => {
        const shouldStringify = ["education", "general", "user"].includes(key);
        formData.append(key, shouldStringify ? JSON.stringify(value) : value);
      });

      await instance.put(`/applicants/${userID}/`, formData);

      dispatch(resetErrors());
      dispatch(getApplicantSimplified(userID));

      history.push(linkWithLanguage(`/applicant/${userID}/profile/`));
    } catch (error) {
      await dispatch(handleError(error));
      error.response
        ? console.error(error.response.data)
        : console.error(error);
    }
  };

export const addCredits = (userData) => async (dispatch) => {
  try {
    const res = await instance.post(`/credits/`, userData);
    return res;
  } catch (error) {
    error.response ? console.error(error.response.data) : console.error(error);
    apiErrorCatcher(error, dispatch);
    return "Rejected";
  }
};
export const addManualHours = (userData) => async (dispatch) => {
  try {
    const res = await instance.post(`applicants/manual-hours/`, userData);
    return res;
  } catch (error) {
    error.response ? console.error(error.response.data) : console.error(error);
    apiErrorCatcher(error, dispatch);
    return "Rejected";
  }
};
export const getManualHours = (params) => async (dispatch) => {
  try {
    const res = await instance.get(`applicants/manual-hours/?${params}`);
    return res;
  } catch (error) {
    error.response ? console.error(error.response.data) : console.error(error);
    apiErrorCatcher(error, dispatch);
    return "failed";
  }
};
export const changeManualHoursStatus = (data) => async (dispatch) => {
  const { id, ...rest } = data;
  try {
    const res = await instance.patch(`applicants/manual-hours/${id}/`, rest);
    return res;
  } catch (error) {
    error.response ? console.error(error.response.data) : console.error(error);
    apiErrorCatcher(error, dispatch);
    return "failed";
  }
};

export const ChangeApplicantToSupervisor = (applicant_id) => async () => {
  try {
    await instance.put(`/applicants/supervisor/`, applicant_id);
    window.location.reload();
  } catch (error) {
    error.response ? console.error(error.response.data) : console.error(error);
  }
};

export const exportCreditList = (payload) => async (dispatch, getState) => {
  try {
    const state = getState();

    const { creditFilters } = state.rootApplicantSlice;
    const queryParams = { ...creditFilters, ...payload };
    const query = "?" + createQuery({ filter: queryParams });
    const response = await getCreditsAPI(query, "blob");
    handleFileDownload(response);
  } catch (error) {
    apiErrorCatcher(error, dispatch);
    return "Rejected";
  }
};
export const downloadLoyacCV = (userID) => async (dispatch) => {
  try {
    const response = await instance.get(`/applicants/download-cv/${userID}/`, {
      responseType: "blob",
    });
    handleFileDownload(response);
  } catch (error) {
    apiErrorCatcher(error, dispatch);
  }
};
export const downloadCertificate = (regId, fullName) => async (dispatch) => {
  try {
    const { data } = await instance.get(
      `applicants/download-certificate/${regId}/`,
      {
        responseType: "blob",
      }
    );
    if (data) {
      const link = document.createElement("a");
      const url = URL.createObjectURL(data);
      link.href = url;
      if (fullName) {
        link.download = `${fullName} - certificate.pdf`;
      } else {
        link.download = "certificate.pdf";
      }

      // Append to the DOM, trigger the download, and remove the element
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up the URL object
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    apiErrorCatcher(error, dispatch);
  }
};
